import { defineStore } from 'pinia'
import {
  GameType,
  GameVariation,
  ItemVisualTypes,
  Shapes2D,
  Shapes3D,
  type Terms,
} from '~/enums'

const getGameStoreKey = (
  subject: string,
  grade: string,
  term: Terms | string,
  gameId: string,
) => `${subject.toLowerCase()}:${grade}:${term}:${gameId}`

const shapeGameTypes = new Set<string>([
  GameType.SHAPE,
  GameType.DISTINGUISH_SHAPES,
  GameType.IDENTIFY_SHAPES,
  GameType.MATCH_SHAPES,
])

const inferShapeVariation = (objects: string[] = []) => {
  const shapes2D = new Set<string>(Object.values(Shapes2D))
  const shapes3D = new Set<string>(Object.values(Shapes3D))
  const has2D = objects.some((object) => shapes2D.has(object))
  const has3D = objects.some((object) => shapes3D.has(object))

  if (has2D && has3D) return GameVariation.DIMENSION_2D_3D
  if (has2D) return GameVariation.DIMENSION_2D
  if (has3D) return GameVariation.DIMENSION_3D
  return undefined
}

export const useGamesStore = defineStore('gamesStore', {
  state: () => ({
    games: {} as Record<string, GameSpecification>,
  }),
  getters: {
    // Return all games as an array.
    allGames: (state) => Object.values(state.games),
    // Filter games by year, subject, and term.
    getGamesByTerm:
      (state) =>
      (grade: string, term: Terms): GameSpecification[] => {
        return Object.values(state.games).filter(
          (game) =>
            game.gameReference.grade === grade &&
            game.gameReference.term === term,
        )
      },
    // Optionally, get a game by its ID.
    gameById:
      (state) =>
      (
        gameId: string,
        grade?: string,
        term?: Terms,
        subject?: string,
      ): GameSpecification | undefined => {
        if (grade && term && subject) {
          return state.games[getGameStoreKey(subject, grade, term, gameId)]
        }

        const matches = Object.values(state.games).filter(
          (game) => game.gameId === gameId,
        )
        return matches.length === 1 ? matches[0] : undefined
      },

    gamesPerWeek:
      (state) =>
      (
        grade: string,
        term: Terms,
        subject?: string,
      ): Record<string, GameSpecification[]> => {
        const gamesPerWeek: Record<string, GameSpecification[]> = {}

        // Group games by week.
        Object.values(state.games).forEach((game) => {
          if (
            !game.gameReference ||
            game.gameReference.grade !== grade ||
            game.gameReference.term !== term ||
            (subject &&
              game.gameReference.subject?.toLowerCase() !== subject) ||
            !game.gameReference.week
          )
            return

          if (!gamesPerWeek[game.gameReference.week]) {
            gamesPerWeek[game.gameReference.week] = []
          }
          gamesPerWeek[game.gameReference.week]?.push(game)
        })

        // Sort the keys by extracting the numeric part (e.g., "week-1" => 1).
        const sortedKeys = Object.keys(gamesPerWeek).sort((a, b) => {
          const aNum = parseInt(a.replace(/[^0-9]/g, ''))
          const bNum = parseInt(b.replace(/[^0-9]/g, ''))
          return aNum - bNum
        })

        // Reassemble into a new object with keys in sorted order.
        const sortedGamesPerWeek: Record<string, GameSpecification[]> = {}
        sortedKeys.forEach((key) => {
          if (gamesPerWeek[key]) {
            sortedGamesPerWeek[key] = gamesPerWeek[key]
          }
        })

        return sortedGamesPerWeek
      },
  },
  actions: {
    clearGames() {
      this.games = {}
    },
    async initializeGames(grade: string, subject: string, term: Terms) {
      try {
        const normalizedSubject = subject.toLowerCase()

        // Mathematics is the currently configured content collection. The
        // collection id preserves the capitalised source-folder name.
        if (normalizedSubject !== 'mathematics') return
        const fetchedGames = await queryCollection(normalizedSubject)
          .where(
            'id',
            'LIKE',
            `${normalizedSubject}/${normalizedSubject}/${grade}%`,
          )
          .all()
        fetchedGames.forEach((game: any) => {
          if (game.game_reference?.term !== term) return

          const match = (game as any).meta.path.match(/\/(week-\d+)/)
          const gameType = game.game_properties?.game_type ?? ''
          const isShapeGame = shapeGameTypes.has(gameType)
          const gameSpec: GameSpecification = {
            gameReference: {
              grade: game.game_reference?.grade,
              term: game.game_reference?.term,
              contentStandard: game.game_reference?.content_standard,
              indicator: game.game_reference?.indicator,
              subject: game.game_reference?.subject,
              strand: game.game_reference?.strand,
              substrand: game.game_reference?.substrand,
              week: match ? match[1] : null,
            },
            gameId: game.game_id,
            gameName: game.game_name,
            gameDescription: game.game_description,
            unlocked: game.unlocked === 'true',
            gameProperties: {
              gameType,
              operators: game.game_properties?.operators ?? [],
              useOperatorName: game.game_properties?.useOperatorName ?? false,
              visualType: game.game_properties?.visual_type,
              questionTypes: game.game_properties?.question_types ?? [],
              uniqueObjects:
                game.game_properties?.unique_objects === true ||
                game.game_properties?.unique_objects === 'true' ||
                game.game_properties?.unique_shapes === true ||
                game.game_properties?.unique_shapes === 'true',
            },
            gameLevels:
              game.game_levels?.map((level: any) => {
                const objects = level.objects ?? level.shapes
                return {
                  level: level.level ?? 1,
                  visualType:
                    level.visual_type ??
                    game.game_properties?.visual_type ??
                    (isShapeGame ? ItemVisualTypes.SHAPES : undefined),
                  range: level.range,
                  value: level.value,
                  stepSize: level.step_size,
                  arrayLength: level.array_length,
                  blanksCount: level.blanks_count,
                  groupSize: level.group_size,
                  questionTypes:
                    level.question_types ??
                    game.game_properties?.question_types ??
                    [],
                  uniqueObjects:
                    level.unique_objects === true ||
                    level.unique_objects === 'true' ||
                    level.unique_shapes === true ||
                    level.unique_shapes === 'true' ||
                    game.game_properties?.unique_objects === true ||
                    game.game_properties?.unique_objects === 'true' ||
                    game.game_properties?.unique_shapes === true ||
                    game.game_properties?.unique_shapes === 'true',
                  gameVariation:
                    level.game_variation ??
                    game.game_properties?.operators?.find((operator: string) =>
                      ['2D', '3D', '2D_3D'].includes(operator),
                    ) ??
                    (isShapeGame ? inferShapeVariation(objects) : undefined),
                  objects,
                  colors: level.colors,
                  hiddenOperand: level.hidden_operand,
                  expressionsPerQuestion: level.expressions_per_question,
                  questions: level.questions?.map((question: any) => ({
                    prompt: question.prompt,
                    options: question.options,
                    correctAnswer: question.correct_answer,
                    explanation: question.explanation,
                  })),
                }
              }) || [],
            coreCompetences: game.core_competences ?? [],
            learningObjectives: game.learning_objectives ?? [],
            generation: game.generation
              ? {
                  foundation: game.generation.foundation,
                  authoredForCoverage:
                    game.generation.authored_for_coverage === true,
                  parameterOptions:
                    game.generation.parameter_options ?? [],
                }
              : undefined,
          }
          this.games[
            getGameStoreKey(
              gameSpec.gameReference.subject,
              gameSpec.gameReference.grade,
              gameSpec.gameReference.term,
              gameSpec.gameId,
            )
          ] = gameSpec
        })
      } catch (error) {
        console.error('Error fetching game content:', error)
      }
    },
  },
})
